import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const cv = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));
const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

export default function WorkshopJoinEvent() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "nv_join_workshop_click"));
                const data = [];
                snap.forEach(docSnap => {
                    const d = docSnap.data();
                    data.push({
                        id: docSnap.id,
                        name: cv(d.name),
                        phone: compactPhone(d.phone_no),
                        username: cv(d.username),
                        workshopName: cv(d.workshop_name),
                        subscriptionPlan: cv(d.subscription_plan),
                        subscriptionStartDate: d.subscription_start_date || null,
                        subscriptionEndDate: d.subscription_end_date || null,
                        createdDate: d.created_date || null,
                    });
                });
                data.sort((a, b) =>
                    (parseDate(b.createdDate)?.getTime() || 0) - (parseDate(a.createdDate)?.getTime() || 0)
                );

                // Deduplicate by phone + workshopName — keep latest (first after sort)
                const seen = new Set();
                const unique = data.filter(r => {
                    if (!parseDate(r.subscriptionStartDate) || !parseDate(r.subscriptionEndDate)) return false;
                    const key = `${r.phone}__${r.workshopName}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });

                setRows(unique);
            } catch (err) {
                console.error("WorkshopJoinEvent fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const filtered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return r.phone.includes(q) || r.name.toLowerCase().includes(q);
    });

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone No": r.phone,
            "Username": r.username,
            "Workshop Name": r.workshopName,
            "Subscription Plan": r.subscriptionPlan,
            "Sub. Start Date": fmtDate(r.subscriptionStartDate),
            "Sub. End Date": fmtDate(r.subscriptionEndDate),
            "Created Date": fmtDateTime(r.createdDate),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Workshop Join Events");
        XLSX.writeFile(wb, "workshop_join_events.xlsx");
    };

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">Workshop Join Events</h3>

                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* SEARCH + EXPORT */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name or phone…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "280px" }}
                        />
                        {search && (
                            <span className="text-muted" style={{ fontSize: "13px" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        )}
                        <button
                            className="btn btn-success ms-auto"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                            style={{ minWidth: "140px", fontWeight: 700 }}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Phone No</th>
                                    <th>Username</th>
                                    <th>Workshop Name</th>
                                    <th>Subscription Plan</th>
                                    <th>Sub. Start</th>
                                    <th>Sub. End</th>
                                    <th>Created Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-muted">
                                            {search ? "No results found" : "No data found"}
                                        </td>
                                    </tr>
                                ) : filtered.map((row, i) => (
                                    <tr key={row.id}>
                                        <td>{i + 1}</td>
                                        <td style={{ fontWeight: 600 }}>{row.name}</td>
                                        <td>{row.phone}</td>
                                        <td style={{ color: "#6b7280" }}>{row.username}</td>
                                        <td>{row.workshopName}</td>
                                        <td>{row.subscriptionPlan}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.subscriptionStartDate)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.subscriptionEndDate)}</td>
                                        <td style={{ whiteSpace: "nowrap", color: "#6b7280" }}>{fmtDateTime(row.createdDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
